import { FC, useState, useEffect, useCallback, useRef } from 'react'
import Gleap from 'gleap'
import * as Sentry from '@sentry/react'
import '@reapit/elements/dist/index.css'
import {
  Button,
  FlexContainer,
  TextArea,
  PageHeader,
  Tile,
  Toggle,
  ElToggleItem,
  Input,
  Grid,
  ColQuarter,
  Select,
  Snack,
  PersistentNotification,
  TextBase,
  CardWrap,
  ColHalf,
  Title,
} from '@reapit/elements'
import Pusher from 'pusher-js'
import useApi from '../hook/useApi'

/* START THEME IMPORTS --------- */
import UpdateSettingsBlock from './updateSettings'
import FinishedBlock from './finished'
import LoginFailedBlock from './loginFailed'
import InProgressBlock from './inProgress'
import SaveToCrm from './saveToCrm'
import ResumeBlock from './resume'
import AutoCallHeader from "./autoCallHeader";
import Notification from "./notification";
import LocationCard from './locationCard';
import ShowNotesToUser from './showNotesToUser';
import OutageMemo from './outageMemo'
import MaintenanceBlock from './maintenance'
/* END THEME IMPORTS --------- */

/* START DEFAULT VARIABLES / SETTINGS --------- */
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
let defaultCrmKey = urlParams.get('crmKey') ?? ''
let defaultCrmUrl = urlParams.get('crmUrl') ?? ''
let defaultDestination = urlParams.get('destination') ?? ''
let defaultEndpoint = urlParams.get('endpoint') ?? 'properties'
let offerId = urlParams.get('offerId') !== 'null' ? urlParams.get('offerId') : null // Temp fix - restore when chrome plugin updated.
let uuid = urlParams.get('uuid') ?? ''
let defaultCallTriggerUrl = urlParams.get('call_trigger_url') ?? ''
let timeoutDuration = 60000
let crm = 'STREET';
/*if (process.env.APP_ENV !== 'production') {
  timeoutDuration = 10000
  uuid = 'c23fad6a-d9ab-4bf9-b065-71a06ef021c2'
  // defaultDestination = '07400123456'
  // defaultCrmKey = 'f7c1d721-3deb-41cf-a679-aed4ca63062c'
  // defaultCrmUrl = 'properties/f7c1d721-3deb-41cf-a679-aed4ca63062c'
  // defaultEndpoint = 'properties'
}*/
/* END DEFAULT VARIABLES / SETTINGS --------- */

interface Task {
  code: string
  name: string
  description?: string
  date_follow_up?: string
}

export const Wrapper: FC = () => {
  const [callInProgress, setCallInProgress] = useState<boolean>(false)
  const [finished, setFinished] = useState<boolean>(false)
  const [incoming, setIncoming] = useState<boolean>(false)
  const [related, setRelated] = useState<any>([])
  const [settingsRequired, setSettingsRequired] = useState<boolean>(false)
  const [loginFailed, setLoginFailed] = useState<boolean>(false)
  const [aiFinished, setAiFinished] = useState<boolean>(false)
  const [objections, setObjections] = useState<any[]>([])
  const [callId, setCallId] = useState<string>('')
  const [userName, setUserName] = useState<string>('')
  const [userEmail, setUserEmail] = useState<string>('')
  const [destination, setDestination] = useState<string>(defaultDestination ?? '')
  const [objectionId, setObjectionId] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [aiSummary, setAiSummary] = useState<string >('')
  const [aiHighlights, setAiHighlights] = useState<string | null>(null)
  const [summaryUsed, setSummaryUsed] = useState<boolean>(true)
  const [summary] = useState<string>('')
  const [crmKey, setCrmKey] = useState<string>(defaultCrmKey ?? '')
  const [contactKey] = useState<string>(defaultCrmKey ?? '')
  const [endpoint] = useState<string>(defaultEndpoint ?? '')
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [validation, setValidation] = useState<string[]>([])
  const [validationItems, setValidationItems] = useState<string[]>([])
  //const [viewing, setViewing] = useState<boolean>(false)
  //const [appraisal, setAppraisal] = useState<boolean>(false)
  const [instruction, setInstruction] = useState<boolean>(false)
  const [applicants, setApplicants] = useState<boolean>(false)
  const [followUpType, setFollowUpType] = useState<string>('both')
  const [followUpRequired, setFollowUpRequired] = useState<boolean>(true)
  const [followUpDate, setFollowUpDate] = useState<string>('')
  const [actionDate, setActionDate] = useState<string>('')
  const [categoryId, setCategoryId] = useState<string>('')
  const [crmUrl, setCrmUrl] = useState<string>(defaultCrmUrl ?? '')
  const [callTriggerUrl, setCallTriggerUrl] = useState<string>(defaultCallTriggerUrl ?? '')
  const [resume, setResume] = useState<any>(false)
  const [callsAwaitingSummary, setCallsAwaitingSummary] = useState<any[]>([])
  const [messageTemplates, setMessageTemplates] = useState<any[]>([])
  const [messageTemplateBody, setMessageTemplateBody] = useState<string>('')
  const [messageTemplateId, setMessageTemplateId] = useState<string>('')
  const [templatePlaceholderValues, setTemplatePlaceholderValues] = useState<Record<string, string>>({})
  const [templatePlaceholders, setTemplatePlaceholders] = useState<string[]>([])
  const [selectedMessageTemplate, setSelectedMessageTemplate] = useState<string>('')
  const [whatsappFollowup, setWhatsappFollowup] = useState<boolean>(false)
  const [stitchEnabled, setStitchEnabled] = useState<boolean>(false)
  const [stitchDefaults, setStitchDefaults] = useState<string[]>([])
  const [resumeId, setResumeId] = useState<string>('')
  const [cancelId, setCancelId] = useState<string>('')
  const [toggleAutoCallHeader, setToggleAutoCallHeader] = useState<boolean>(true)
  const [disableFollowUp, setDisableFollowUp] = useState<boolean>(false)
  const [locationSelected, setLocationSelected] = useState<any>(false)
  const [relatedPropertySelectionSource, setRelatedPropertySelectionSource] = useState<string>('default')
  const [displayLoadingText, setDisplayLoadingText] = useState<string>('Loading Details')
  const [postVoicemailSummary, setPostVoicemailSummary] = useState<boolean>(false)
  const [isVoicemail, setIsVoicemail] = useState<boolean>(false)
  const [userForcedVoicemailNotes, setUserForcedVoicemailNotes] = useState<boolean>(false)
  const [userId, setUserId] = useState<string>('')

  const [outageMessage, setOutageMessage] = useState<string>('')
  const [outageSeverity, setOutageSeverity] = useState<number>(0)

  const disableCalls = false
  const version = '25.12.4'
  const dateSetRef = useRef(false);

  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)

  const { makeRequest, setBearerToken, bearerToken, setXAgency, createSessionRequest } = useApi()

  const apiPath = process.env.REACT_APP_PP_API_URL
  const pusherKey = process.env.REACT_APP_PP_PUSHER_KEY
  const gleapInitialized = useRef(false);

  const AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED';
  const CALL_IN_PROGRESS = 'CALL_IN_PROGRESS';
  const CALL_FAILED = 'CALL_FAILED';
  const fetchMessageTemplates = async () => {
    const response = await makeRequest('stitch/message-templates', 'GET')
    const results = response.data
    setMessageTemplates(results)
  }

  useEffect(() => {
    authenticateUser()
  }, [])

  useEffect(() => {
    if (Gleap.isUserIdentified()) {
      const customData = {
        callInProgress: callInProgress.toString(),
        finished: finished.toString(),
        destination: destination || '',
        aiFinished: aiFinished.toString(),
        objections: objections.length.toString(),
        callId: callId.toString(),
        notes: notes || '',
        summary: summary || '',
        crmKey: crmKey || '',
        loading: loading.toString(),
        saving: saving.toString(),
        validation: JSON.stringify(validation),
        validationItems: JSON.stringify(validationItems),
        instruction: instruction.toString(),
        followUpType: followUpType || '',
        followUpRequired: followUpRequired.toString(),
        followUpDate: followUpDate || '',
        actionDate: actionDate || '',
        related: JSON.stringify(related),
        categoryId: categoryId.toString(),
        crmUrl: crmUrl || '',
        version: version || '',
      }

      Gleap.attachCustomData(customData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callInProgress,
    finished,
    destination,
    aiFinished,
    objections,
    callId,
    notes,
    summary,
    crmKey,
    loading,
    saving,
    validation,
    validationItems,
    instruction,
    followUpType,
    followUpRequired,
    followUpDate,
    actionDate,
    related,
    categoryId,
    crmUrl,
    version,
  ]) // Empty dependency array to run only once on component mount
  /* END GLEAP INTEGRATION --------- */


  useEffect(() => {
    if (bearerToken) {
      loadObjections()
      if (contactKey) {
        // if we have a contact try and call it
        startVoipCall(contactKey, endpoint)
      }
      if (stitchEnabled && messageTemplates && messageTemplates.length < 1) {
        fetchMessageTemplates()
      }
    }
  }, [bearerToken])

  const setNoteType: (useSummary: boolean) => void = useCallback(async (useSummary: boolean) => {
    setSummaryUsed(useSummary);
    if (useSummary) {
      setNotes(aiSummary ?? 'No summary generated')
    } else {
      setNotes(aiHighlights ? aiHighlights.replace(/\\n/g, '\n') : 'No highlights generated')
    }
  }, [aiSummary, aiHighlights])

  useEffect(() => {
    if((resumeId || resume) && !gleapInitialized.current) {
      Gleap.initialize('TR7lgdpnWjMLKcAoNUFtSXFKBRvz7gLq');
      gleapInitialized.current = true;
    }

    if (resumeId) {
      const resumeItem = resume.find((item: any) => item.id === resumeId)

      if (!resumeItem) {
        return
      }

      // if we don't they may want to resume
      // if resume record exists
      setCallId(resumeId)
      setLoading(false)
      setDestination(resumeItem.destination)
      setAiFinished(resumeItem.summary ? true : false)
      setCallInProgress(false)
      setCrmKey(resumeItem.crm_key)
      setCrmUrl(resumeItem.crm_url.replace(/^\/+/, ''))
      setAiSummary(resumeItem.summary ?? 'No summary generated')
      setAiHighlights(resumeItem.highlights ?? null)
      setIsVoicemail(resumeItem.is_voicemail ?? false)

      try {
        setRelated(JSON.parse(resumeItem?.crm_save_locations ?? "[]"))
      } catch (error) {
        console.warn('Error parsing crm_save_locations', error)
        setRelated([])
      }
      setNotes(resumeItem.summary ?? 'No summary generated')
      //setViewing(resumeItem.viewing ?? false)
      setInstruction(resumeItem.instruction ?? false)

      loadObjections()
      if (resumeItem.call_direction == 'inbound') {
        setIncoming(true)
        setFollowUpRequired(false)
      } else {
        setIncoming(false)
      }

    }
  }, [resumeId, resume])

  useEffect(() => {
    if (!Gleap.isUserIdentified() && userEmail && userName && userId) {
      Gleap.identify(userId, { email: userEmail.toString(), name: userName.toString() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId, resume, userEmail, userName, userId]) // Empty dependency array to run only once on component mount

  useEffect(() => {
    if (cancelId) {
      const cancelItem = resume.find((item: any) => item.id === cancelId)

      if (!cancelItem) {
        return
      }

      const callRequest = {
        call_ended: true,
        call_state: 'Call Ended - STREET App not saved',
        follow_up_synced: true,
        follow_up_type: 'none',
        note_synced: true,
      }

      try {
        makeRequest('calls/' + cancelId, 'PUT', callRequest)
      } catch (error) {
        setLoading(false)
        setSaving(false)
        Sentry.captureException(error, {
          extra: {
            message: `Failed to update call - Client Unaware - ${error}`,
            callId: cancelId,
            crmKey: cancelItem.crm_key,
          },
        })
      }

      setResume((prevResume: any) => prevResume.filter((item: any) => item.id !== cancelId))
    }
  }, [cancelId])

  useEffect(() => {
    if (callId !== '') {
      const pusher = new Pusher(pusherKey as string, {
        cluster: 'eu',
        forceTLS: true,
      })

      const channel = pusher.subscribe(callId)

      document.addEventListener('sessionEnded', () => {
        pusher.disconnect()
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current)
          timeoutIdRef.current = null
        }
      })

      channel.bind('note.synced', (data: any) => {
        if (data.note_synced === true) {
          setSaving(false)
          setFinished(true)
          pusher.unsubscribe(callId)
          pusher.disconnect()
        }
      })

      // Cleanup the pusher connection when the component unmounts or callId changes
      return () => {
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current)
          timeoutIdRef.current = null
        }
        pusher.unsubscribe(callId)
        pusher.disconnect()
      }
    }
  }, [callId, aiFinished, crmUrl, dateSetRef])

  const onSubmit: () => void = useCallback(async () => {
    setValidation([])
    setValidationItems([])
    let hasErrors = false

    if (followUpRequired && !followUpDate) {
      setValidationItems((prevItems) => [...prevItems, 'followUpDate'])
      setValidation((prevItems) => [...prevItems, 'You need to choose and follow up date'])
      hasErrors = true
    }

    if (hasErrors) {
      return
    }

    setSaving(true)

    const request = {
      status: 'Called',
      crm_key: crmKey,
      crm_url: crmUrl,
      call_id: callId,
      related_property_selection_source: relatedPropertySelectionSource,
    } as { [key: string]: any };

    const callRequest = {
      call_ended: true,
      call_state: 'Call Ended - CRM Plugin',
      notes: notes,
      highlights: aiHighlights,
      summary_used: summaryUsed,
      follow_up_type: 'followUp',
      instruction: instruction,
      objection_id: objectionId,
      whatsapp: true,
      message_template_id: messageTemplateId,
      message_template_body: messageTemplateBody,
    } as { [key: string]: any };

    if (isVoicemail && postVoicemailSummary && userForcedVoicemailNotes) {
      callRequest['force_summarise'] = true
    }

    if (followUpRequired && followUpDate) {
      request['date_follow_up'] = followUpDate
      callRequest['chosen_date'] = followUpDate
    } else {
      request['date_follow_up'] = null
    }

    try {
      await makeRequest('look-up-lead', 'POST', request)
    } catch (error) {
      setLoading(false)
      setSaving(false)
      Sentry.captureException(error, {
        extra: {
          message: `Failed to post update data to "look-up-load" from STREET plugin - Client Unaware - ${error}`,
          callId: callId,
          crmKey: crmKey,
          request: request
        },
      })
    }

    try {
      await makeRequest('calls/' + callId, 'PUT', callRequest)
    } catch (error) {
      setLoading(false)
      // setError(`Failed to update call - ${error}`)
      Sentry.captureException(error, {
        extra: {
          message: `Failed to update call - Client Unaware - ${error}`,
          callId: callId,
          crmKey: crmKey,
        },
      })
    }

    // Once the Notes go up to the CRM a pusher event is fired which
    // will close the modal on the plugin!


  }, [
    crmKey,
    crmUrl,
    callId,
    notes,
    instruction,
    objectionId,
    followUpType,
    actionDate,
    followUpDate,
    followUpRequired,
  ])

  const onCancelResume: () => void = useCallback(async () => {
    setResumeId('')
    setRelated([])
    setLocationSelected(false)
    setCallId(resumeId)
    setAiSummary('')
    setAiHighlights(null)
    setIsVoicemail(false)
    setUserForcedVoicemailNotes(false)
    setPostVoicemailSummary(false)
    setDestination('')
    setAiFinished(false)
    setCallInProgress(false)
    setCrmKey('')
    setCrmUrl('')
    setNotes('')
    setSummaryUsed(true)
  }, [])

  const onAbandon: () => void = useCallback(async () => {
    setFinished(true)

    const request = {
      status: 'Called',
      crm_key: crmKey,
      crm_url: crmUrl,
      call_id: callId,
    }

    const callRequest = {
      call_ended: true,
      call_state: 'Call Ended - CRM PLUGIN -  App not saved',
      notes: notes,
      highlights: aiHighlights,
      instruction: instruction,
      objection_id: objectionId,
      follow_up_synced: true,
      follow_up_type: 'none',
      note_synced: true,
    }

    try {
      await makeRequest('calls/' + callId, 'PUT', callRequest)
    } catch (error) {
      setLoading(false)
      setSaving(false)
      Sentry.captureException(error, {
        extra: {
          message: `Failed to update call - Client Unaware - ${error}`,
          callId: callId,
          crmKey: crmKey,
        },
      })
    }

    setSaving(false)
    setFinished(true)
  }, [
    crmKey,
    crmUrl,
    callId,
    notes,
    instruction,
    objectionId,
    followUpType,
    actionDate,
    followUpDate,
    followUpRequired,
  ])

  // Update category ID for objections when crm key selected
  // until now we don't know if its sales or lettings as we have called
  // the contact record
  useEffect(() => {
    console.log(related);
    const matchingListing = related.find((listing: { crm_key: string }) => listing.crm_key === crmKey)
    // If a match is found, set the category_id
    if (matchingListing) {
      console.log(matchingListing);
      setCrmUrl(matchingListing.crm_url ?? '')
      setCategoryId(matchingListing.category ?? '')
      if (matchingListing.type && matchingListing.type == 'Applicants') {
        setApplicants(true)
      }

      const request = {
        status: 'Called',
        crm_key: crmKey,
        crm_url: matchingListing.crm_url,
        call_id: callId,
        related_property_selection_source: relatedPropertySelectionSource,
      }

      makeRequest('look-up-lead', 'POST', request)
        .catch(error => {
          Sentry.captureException(error, {
            extra: {
              message: `Failed to "look-up-lead" related leads from RPS - Client Unaware - ${String(error)}`,
              callId,
              crmKey,
              status: error?.status,
              body: error?.body,
            },
          });
        });

    }
  }, [crmKey, related, callId, relatedPropertySelectionSource])

  const authenticateUser = async () => {
    try {

      if (!uuid) {
        return;
      }
      const authRequest = await fetch(apiPath + '/login-street-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: uuid,
          resume: crmKey ? false : true,
        }),
      })

      const auth = await authRequest.json()
      setBearerToken(auth['bearer-token'])
      setXAgency(auth['x-prospector-agency'])
      setUserName(auth['x-prospector-user-name'])
      setUserEmail(auth['x-prospector-user-email'])
      setStitchEnabled(auth['x-stitch-enabled'])
      setFollowUpType(auth['x-user-follow-up-type'] || 'both')
      setDisableFollowUp(auth['x-prospector-disable-follow-up'])
      setFollowUpRequired(!auth['x-prospector-disable-follow-up'])
      setOutageMessage(auth['x-prospector-outage-message'])
      setOutageSeverity(auth['x-prospector-outage-severity'])
      setPostVoicemailSummary(auth['x-prospector-post-voicemail-summary'])
      setUserId(auth['x-prospector-user-id'])

      if (auth.message && auth.message.login) {
        setLoading(false)
        setLoginFailed(true)
        sendToContentScript(AUTHENTICATION_FAILED)
        return;
      }

      if (!contactKey && auth['x-call-resume'] && auth['x-call-resume'] !== '') {
        try {
          let resumeData = JSON.parse(auth['x-call-resume']);
          setResume(resumeData)
          if (Array.isArray(resumeData) && resumeData.length > 0 && resumeData[0]?.summary) {
            setAutoResume(resumeData);
          }
        } catch (e) {
          console.error('Invalid x-call-resume JSON', e);
        }
      }

      if (!contactKey && auth['x-call-awaiting-summary'] && auth['x-call-awaiting-summary'] !== '') {
        try {
          let result = JSON.parse(auth['x-call-awaiting-summary']);
          setCallsAwaitingSummary(result)
        } catch (e) {
          console.error('Invalid x-call-awaiting-summary JSON', e);
        }
      }

      if (auth['x-prospector-rps-app-settings']) {
        const settings = auth['x-prospector-rps-app-settings']
        try {
          let parsedSettings = settings
          if (typeof settings === 'string') {
            parsedSettings = JSON.parse(settings)
          }
          // Check if either display_number or routing_number is missing or empty
          if (!parsedSettings.display_number || !parsedSettings.routing_number) {
            setSettingsRequired(true)
            const authRequest = await fetch(apiPath + '/show-error', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                token: uuid + crmKey,
              }),
            })
          }
        } catch (error) {
          setSettingsRequired(true) // Optionally set settings required on parse error
        }
      } else {
        setSettingsRequired(true)
      }

    } catch (error) {
      sendToContentScript(AUTHENTICATION_FAILED)
      setLoading(false)
      setLoginFailed(true)
      Sentry.captureException(error, {
        extra: {
          callId: callId,
          crmKey: crmKey,
        },
      })
    }
  }

  const setAutoResume = function (resumeData: any) {

    if (!resumeData || !Array.isArray(resumeData) || resumeData.length === 0) {
      console.error('Invalid resumeData:', resumeData);
      return;
    }

    if (resumeData && resumeData[0].updated_at) {
      const createdAt = new Date(resumeData[0].updated_at); // parsed as UTC because of the Z
      const now = new Date();
      const diffMs = now.getTime() - createdAt.getTime(); // milliseconds difference
      const diffMinutes = diffMs / (1000 * 60);
      if (diffMinutes <= 10) {
        let relatedData = [];
        try {
          relatedData = JSON.parse(resumeData[0].crm_save_locations ?? "[]");
        } catch (e) {
          console.error("Failed to parse CRM save locations:", e);
        }
        setRelated(relatedData);
        setResumeId(resumeData[0].id ?? '');
        setCrmUrl(resumeData[0].crm_url ?? '');
      }
    }
  }


  const setFollowUpDateValue = function (date: string) {
    setFollowUpDate(date);
    console.log('setFollowUpDateValue', date);
    dateSetRef.current = true;
  }


  const setActionDateValue = function (date: string) {
    setActionDate(date);
    console.log('setActionDateValue', date);
    dateSetRef.current = true;
  }


  const loadObjections = async () => {
    try {
      const objectionResponse = await makeRequest('objections', 'GET')
      setObjections(objectionResponse.data)
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Objections Failed',
          callId: callId,
          crmKey: crmKey,
        },
      })
    }
  }

  const startVoipCall = async (contact_key: string, endpoint: string) => {
    if (callInProgress || settingsRequired) return

    if (outageSeverity && outageSeverity == 1) {
      return
    }

    // on backedn check if phone number belongs to a vendor and related to this property
    // if so flag so its saved in both locations.

    try {
      setCallInProgress(true)

      // Trigger actual call
      if (disableCalls) {
        setLoading(false)
        setCallInProgress(false)
      } else {
        let callResponseData: any = [];

        if (endpoint === 'recall') {

          const url = `calls/${contact_key}/recall`;

          callResponseData = await makeRequest(url, 'POST', {})
          setCallId(callResponseData?.data?.id)
          setStitchDefaults([
            callResponseData?.data?.vendor_name,
            callResponseData?.data?.lead_address,
            callResponseData?.data?.full_name,
          ])
        } else {
          const sessionResponse = await createSessionRequest(contact_key, endpoint, defaultCrmUrl, destination)

          callResponseData = await makeRequest('calls', 'POST', {
            lead_id: sessionResponse.data[0].id,
            vendor_id: sessionResponse.data[0].vendor.id,
            session_id: sessionResponse.session.id,
            offer_id: offerId,
            destination: destination,
            endpoint: endpoint,
            call_trigger_url: callTriggerUrl
          })
          setCallId(callResponseData?.data?.id)
          setStitchDefaults([
            callResponseData?.data?.vendor_name,
            callResponseData?.data?.lead_address,
            callResponseData?.data?.full_name,
          ])
        }
        sendToContentScript(CALL_IN_PROGRESS)
      }
    } catch (error) {
      sendToContentScript(CALL_FAILED)
      setLoading(false)
      const authRequest = await fetch(apiPath + '/show-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: uuid + crmKey,
        }),
      })
      Sentry.captureException(error, {
        extra: {
          message: `Failed to startVoipCall - Customer Informed Lead Incorrect ${error}`,
          callId: callId,
          crmKey: crmKey,
        },
      })
    } finally {
      setCallInProgress(false)
      fetchLeadData(crmUrl)
    }
  }

  const fetchLeadData = async (crm_url: string) => {
    try {
      const results = await makeRequest('look-up-street-lead/' + crmUrl, 'GET', null)
      const leadData = results.data
      if (leadData && leadData.category_id) {
        setCategoryId(leadData.category_id);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'fetchLeadData Failed',
          callId: callId,
          crmKey: crmUrl,
        },
      })
    }
  }

  const onChangeMessageTemplate = (templateId: any) => {
    setMessageTemplateId(templateId)
    const template: string = messageTemplates.find((m) => m.id === templateId)?.message ?? ''

    if (template) {
      const placeholders = template.match(/\{\{\d+\}\}/g) || []

      setTemplatePlaceholders(placeholders)

      const initialPlaceholderValues: Record<string, string> = {}
      placeholders.forEach((placeholder, index) => {
        initialPlaceholderValues[placeholder] = stitchDefaults[index] ?? ''
      })

      setTemplatePlaceholderValues(initialPlaceholderValues)

      setSelectedMessageTemplate(template)
      const messageBody = getFilledTemplate(template, placeholders, initialPlaceholderValues)
      setMessageTemplateBody(messageBody)
    }
  }

  const handlePlaceholderChange = (value: any, placeholder: string) => {
    setTemplatePlaceholderValues({
      ...templatePlaceholderValues,
      [placeholder]: value,
    })
    const messageBody = getFilledTemplate()
    setMessageTemplateBody(messageBody)
  }

  const getFilledTemplate = (
    selectedTemplate: string = selectedMessageTemplate,
    placeholders: string[] = templatePlaceholders,
    placeholderValues: Record<string, string> = templatePlaceholderValues,
  ): string => {
    let filledTemplate = selectedTemplate
    if (filledTemplate.length === 0) return ''

    placeholders.forEach((placeholder) => {
      filledTemplate = filledTemplate.replace(placeholder, placeholderValues[placeholder] || placeholder)
    })
    return filledTemplate
  }

  const generatePageTitle = () => {
    let message = '';

    if (resume && !contactKey) {
      if (incoming) {
        message = `Save incoming call from: ${destination}`;
      } else {
        message = `Resume call details for: ${destination}`;
      }
    }
    else {
      message = 'Call finished';
    }

    return message;
  }
  const generatePageSubTitle = () => {
    let message = '';

    if (resume && !contactKey) {
      message = 'Please continue to save these details to your CRM before attempting another call';
    }
    else {
      message = 'Now the call has ended please confirm the details to go back into your CRM.';
    }

    return message;
  }

  useEffect(() => {
    if (destination) {
      setDisplayLoadingText("Call Connecting - Once finished We will notify you when a summary is available")
    }
  }, [destination])

  // show when full outage in the loading call area.
  if (outageSeverity && outageSeverity == 1 && destination)
    return (
      <InProgressBlock displayLoadingText={outageMessage} />
    )

  // show when full outage in the loading call area.
  if (outageSeverity && outageSeverity == 1 && !destination)
    return (
      <MaintenanceBlock outageMessage={outageMessage} />
    )

  if (finished)
    return (
      <FinishedBlock />
    )

  if (loginFailed)
    return (
      <LoginFailedBlock />
    )

  if (settingsRequired)
    return (
      <UpdateSettingsBlock />
    )
  if (crmUrl.indexOf('voip') !== -1 && related.length <= 0) // voip with no related to save (orphaned)
    return (
      <ShowNotesToUser
        notes={notes}
        resumeId={resumeId ?? null}
        onCancelResume={onCancelResume}
        onAbandon={onAbandon}
      />
    )

  if (resume && !resumeId)
    return (
      <ResumeBlock resumeData={resume} callInProgressData={callsAwaitingSummary} setResumeId={setResumeId} setCancelId={setCancelId} outageMessage={outageMessage} outageSeverity={outageSeverity} />
    )

  if (!crmKey && !destination && !resume && bearerToken)
    return (
      <InProgressBlock displayLoadingText={'Congratulations! There are no more calls left to resume!'} />
    )


  if (loading)
    return (
      <InProgressBlock displayLoadingText={displayLoadingText} />
    )


  if (saving)
    return (
      <SaveToCrm />
    )

  return (
    <div className='relative'>
      <PageHeader
        avatar={{
          src: 'complete.png',
          type: 'image',
        }}
        hasMaxWidth
        pageTitle={{
          children: generatePageTitle(),
          hasBoldText: true,
        }}
        pageSubtitle={{
          children: generatePageSubTitle()
        }}
      />
      {outageMessage ? <OutageMemo outageMessage={outageMessage} outageSeverity={outageSeverity} /> : null}
      <FlexContainer hasMaxWidth isFlexColumn>
        <Tile>
          {validation.length > 0 ? (
            <Snack style={{ width: '100%', marginBottom: '20px' }} intent="danger" icon="warning">
              <div
                dangerouslySetInnerHTML={{
                  __html: validation.join('<br />'),
                }}
              />
            </Snack>
          ) : null}

          {related.length > 0 && !locationSelected ? (
            <div>
              <Notification
                storageKey="notificationSavingLocation"
                message={
                  <>
                    <strong>New Feature</strong> - Please choose where to save this call.
                  </>
                }
              />
              <div>
                <Title>Select location to save notes</Title>
                {Array.isArray(related) && related.every((item) => typeof item === 'object' && item !== null)
                  ? related.map((listing, index) => (
                    <div
                      key={index}
                      className="propertyCard"
                      onClick={() => {
                        setLocationSelected(listing)
                        setCrmKey(listing.crm_key)
                        setRelatedPropertySelectionSource('user')
                      }}
                    >
                      <LocationCard listing={listing} />
                    </div>
                  ))
                  : null}
              </div>
              <Button intent="danger" className="cancel-autoresume" type="button" onClick={onCancelResume}>
                Cancel Resume
              </Button>
            </div>
          ) : (
            <>
              {resumeId && (
                <>

                  {(
                    <Grid className="">

                      <ColQuarter className="el-mb6 el-pl5 el-pr5">
                        <p className="el-mb3">Instruction Won</p>
                        <Toggle
                          key={instruction ? 'yes' : 'no'}
                          style={{ width: '100%' }}
                          hasGreyBg
                          id="instruction"
                          defaultChecked={instruction}
                          onChange={(e) => setInstruction(e.currentTarget.checked)}
                        >
                          <ElToggleItem className="toggle-yes">Yes</ElToggleItem>
                          <ElToggleItem className="toggle-no">No</ElToggleItem>
                        </Toggle>
                      </ColQuarter>

                      {(!disableFollowUp) && (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className="el-mb3">Follow Up Required</p>
                          <Toggle
                            key={followUpRequired ? 'yes' : 'no'}
                            style={{ width: '100%' }}
                            hasGreyBg
                            id="followUpRequired"
                            defaultChecked={followUpRequired}
                            onChange={(e) => setFollowUpRequired(e.currentTarget.checked)}
                          >
                            <ElToggleItem className="toggle-yes">Yes</ElToggleItem>
                            <ElToggleItem className="toggle-no">No</ElToggleItem>
                          </Toggle>
                        </ColQuarter>
                      )}

                      {followUpRequired && (
                        <ColQuarter className="el-mb6 el-pl5 el-pr5">
                          <p className={`el-mb3 ${validationItems.includes('followUpDate') ? 'error' : ''}`}>
                            Date of Follow Up
                          </p>
                          <Input
                            className="el-w12"
                            type="date"
                            value={followUpDate}
                            onChange={(e) => setFollowUpDateValue(e.target.value)}
                          />
                        </ColQuarter>
                      )}
                    </Grid>
                  )}
                </>
              )}

              {disableFollowUp && (
                <div className="el-pl5 el-pr5 el-mt10">
                  <PersistentNotification intent="low" className="el-mb6" isExpanded isFullWidth isInline>
                    <div className="el-mb5">
                      <TextBase hasBoldText>Follow Up Disabled</TextBase>
                    </div>
                    <p>
                      Please note that follow-up functionality is currently disabled for your agency. As a result, a follow-up will not be scheduled for this call.
                      If you would like this feature to be enabled, please contact your administrator.
                    </p>

                  </PersistentNotification>
                </div>
              )}

              {isVoicemail && !postVoicemailSummary ? (
                <div className={`el-pl5 el-pr5 el-pt5 el-mt5`}>
                  <div style={{ background: '#ffffff', border:'1px solid #F08E32' }} className="el-card-focussed el-card-wrap el-mb5">
                    <p className="el-mb2" style={{fontWeight:'500'}}>
                      We think this might be a voicemail?
                    </p>
                    <p className="el-mb5">
                      Your agency has opted not to post voicemail notes by default. Use the button below if you'd like to leave detailed notes.
                    </p>
                    <Button
                      intent="pending"
                      type="button"
                      className='edit'
                      onClick={function () {
                        setPostVoicemailSummary(true)
                        setUserForcedVoicemailNotes(true)
                      }}
                    >
                      Edit Detailed Notes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={`el-pl5 el-pr5 el-pt5 el-mt5`}>
                  <div className="el-flex el-flex-row el-flex-justify-between">
                    <span className="el-mb3">
                      <TextBase hasBoldText>Call Notes</TextBase>
                    </span>
                    <div className="el-mb3 el-flex el-flex-row">
                      {aiHighlights ? (
                        <div className='el-pl5'>
                          <Toggle
                            style={{ width: '100%' }}
                            hasGreyBg
                            id="noteType"
                            defaultChecked={true}
                            onChange={(e) => setNoteType(e.currentTarget.checked)}
                          >
                            <ElToggleItem>Summary</ElToggleItem>
                            <ElToggleItem>Highlights</ElToggleItem>
                          </Toggle></div>
                      ) : null}
                    </div>
                  </div>
                  <TextArea
                    key={`${resumeId}:${!!notes}`}
                    value={summaryUsed
                        ? aiSummary
                        : aiHighlights
                            ? aiHighlights.replace(/\\n/g, '\n')
                            : 'No highlights generated'
                    }
                    className="el-mb7"
                    onChange={(e) => {
                        if (summaryUsed) {
                            setAiSummary(e.target.value)
                            setNotes(e.target.value)
                        } else {
                            setAiHighlights(e.target.value)
                            setNotes(e.target.value)
                        }
                    }
                    }
                    style={{ width: '100%', height: '300px' }}
                  />
                    {aiSummary && aiHighlights && (
                        <small
                            style={{
                                fontSize: '14px',
                                color: '#2563eb', // visible blue
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        > ℹ️ Call <strong>{summaryUsed ? 'summary' : 'highlights'}</strong> will be treated as the call note.</small>
                    )}
                  {userForcedVoicemailNotes ? (
                      <Button
                        className="el-mb5 el-mt5"
                        intent="danger"
                        type="button"
                        style={{ background: '#ffa000', color: '#ffffff', borderColor: '#ffa000' }}
                        onClick={function () {
                          setPostVoicemailSummary(false)
                          setUserForcedVoicemailNotes(false)
                        }}
                      >
                        Cancel Detailed Notes
                      </Button>
                    ) : null}
                </div>
              )}

              {!resume.incoming_id && !instruction && !applicants && crmKey && categoryId && (
                <div className="el-pl5 el-pr5 el-border-grey-t el-pt7">
                  <p className={`el-mb3 el-mt-5 ${validationItems.includes('objectionId') ? 'error' : ''}`}>
                    Objection Met
                  </p>
                  <Select className="el-mb6 el-w-full" onChange={(e) => setObjectionId(e.target.value)} required>
                    <option data-testid="option" value="">
                      Select an option
                    </option>
                    {objections
                      .filter((objection) => categoryId ? objection.category_id === categoryId : true)
                      .map((objection) => (
                        <option key={objection.id} data-testid="option" value={objection.id}>
                          {objection.name}
                        </option>
                      ))}
                  </Select>
                </div>
              )}

              {stitchEnabled && destination && destination.startsWith('07') ? (
                <label
                  className="el-pl5 el-pr5 el-mb8 el-flex el-flex-row el-border-grey-t el-pt5"
                  style={{ alignItems: 'center' }}
                >
                  <Input
                    className="el-w12"
                    type="checkbox"
                    name="whatsapp"
                    onChange={(e) => setWhatsappFollowup(e.target.checked)}
                  />
                  <p className="el-pl5">
                    <TextBase hasBoldText>Send Whatsapp Follow up</TextBase>
                  </p>
                </label>
              ) : null}

              {whatsappFollowup ? (
                <CardWrap className="el-card-focussed">
                  <Grid>
                    <ColHalf>
                      <div className="el-pl5 el-pr5">
                        <p className="el-mb3">
                          <TextBase hasBoldText>Message Template</TextBase>
                        </p>
                        <Select
                          className="el-mb6 el-w-full"
                          onChange={(e) => onChangeMessageTemplate(e.target.value)}
                          required
                        >
                          <option data-testid="option" value="">
                            Select an option
                          </option>
                          {Array.isArray(messageTemplates) && messageTemplates.length > 0 ? (
                            messageTemplates
                              .filter((m) => m.name?.startsWith('phone_') || m.name?.startsWith('prospector_'))
                              .map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.name}
                                </option>
                              ))
                          ) : (
                            <option disabled>No templates available</option>
                          )}
                        </Select>
                      </div>

                      {templatePlaceholders.length > 0 && (
                        <>
                          {templatePlaceholders.map((placeholder, index) => (
                            <div key={index} className="el-pl5 el-pr5 el-pb5">
                              <TextBase hasBoldText>
                                Enter text for Template Part {index} - {placeholder}:
                              </TextBase>
                              <Input
                                className="el-w12"
                                type="text"
                                value={templatePlaceholderValues[placeholder] as string}
                                onChange={(e) => handlePlaceholderChange(e.target.value, placeholder)}
                                onBlur={(e) => handlePlaceholderChange(e.target.value, placeholder)}
                              />
                            </div>
                          ))}
                        </>
                      )}
                    </ColHalf>
                    <ColHalf>
                      {messageTemplateId ? (
                        <div className="el-pl5 el-pr5">
                          <p className="el-mb3">
                            <TextBase hasBoldText>Message Body</TextBase>
                          </p>
                          <TextArea
                            value={getFilledTemplate()}
                            style={{ width: '100%', height: '200px' }}
                            disabled={true}
                          >
                            {getFilledTemplate()}
                          </TextArea>
                        </div>
                      ) : null}
                    </ColHalf>
                  </Grid>
                </CardWrap>
              ) : null}

              <div className="el-pl5 el-pr5 el-mt10">
                <Button className="el-mr5" intent="primary" type="button" onClick={onSubmit}>
                  Save to CRM
                </Button>
                <Button className="el-mr5" intent="danger" type="button" onClick={onAbandon}>
                  Do not save to CRM
                </Button>
              </div>
            </>
          )}
        </Tile>
      </FlexContainer>
      {resumeId && (
        <>
          <Button intent="danger" type="button" className='cancel-autoresume' onClick={onCancelResume}>
            Cancel Resume
          </Button>
        </>
      )}
    </div>
  )
}

// Function to send data to content script
const sendToContentScript = (data: any) => {
  window.parent.postMessage(
    { action: "PP_NOTIFICATION", data },
    "*"
  );
};


export default Wrapper
